import { useState, useEffect, useCallback  } from "react";
import { createContext } from "react";
import { useAuth } from "../Hooks/useAuth";
import { api } from "../services/api";

export const CartContext = createContext({});

export function CartContextProvider(props) {
  const { user } = useAuth();
  const [showCartPopUp, setShowCartPopUp] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [cartItemsCounter, setCartItemsCounter] = useState(0);
  const [userCartBatches, setUserCartBatches] = useState([]);

  const hidePopupDebounced = useCallback(() => {
    setTimeout(() => {
      setShowCartPopUp(false);
    }, 2000);
  }, []);

  useEffect(() => {
    if (user) {
      fetchUserCartCount();
    } else {
      const guestCart = JSON.parse(
        localStorage.getItem("guestCart") || "[]"
      );
      setCartItemsCounter(guestCart.length);
    }
  }, [user]);

  async function fetchUserCartCount() {
    try {
      const response = await api.get(`/api/v1/cart_batches?user_id=${user.id}`);
      const batches = response.data.cart_batches || [];
      setCartItemsCounter(batches.length);
      setUserCartBatches(batches);
    } catch (error) {
      console.error("Error fetching user cart count:", error);
    }
  }

  function handleSetSelectedProduct(e, product, batch, finalPrice, quantity) {
    e.preventDefault();
    if (user) {
      handleCreateCart(user, product, batch, finalPrice, quantity)
    } else {
      handleAddToCart(product, batch, finalPrice, quantity);
    }
  }

  async function migrateGuestCartToUser(targetUser) {
    const guestCart = JSON.parse(localStorage.getItem("guestCart") || "[]");

    // Fetch current server cart once so we can dedup
    let serverBatches = [];
    try {
      const res = await api.get(`/api/v1/cart_batches?user_id=${targetUser.id}`);
      serverBatches = res.data.cart_batches || [];
    } catch (_) {}

    for (const item of guestCart) {
      const existing = serverBatches.find(cb => cb.batch_id === item.batch_id);
      if (existing) {
        const d = new FormData();
        d.append("cart_batch[quantity]", existing.quantity + item.quantity);
        d.append("cart_batch[amount]", Number(existing.amount) + Number(item.amount));
        await api.put(`/api/v1/cart_batches/${existing.id}`, d);
      } else {
        await api.post("/api/v1/cart_batches", {
          user_id: targetUser.id,
          batch_id: item.batch_id,
          amount: item.amount,
          quantity: item.quantity,
        });
      }
    }

    localStorage.removeItem("guestCart");
    return true;
  }

  function handleAddToCart(product, batch, finalPrice, quantity) {
    const cartData = localStorage.getItem('guestCart');
    let guestCart = [];
    try {
      if (cartData) guestCart = JSON.parse(cartData) || [];
    } catch (error) {
      console.error("Error parsing guestCart from localStorage:", error);
      guestCart = [];
    }

    const existingIdx = guestCart.findIndex(item => item.batch_id === batch.id);
    if (existingIdx >= 0) {
      guestCart[existingIdx].quantity = Number(guestCart[existingIdx].quantity) + Number(quantity);
      guestCart[existingIdx].amount   = Number(guestCart[existingIdx].amount)   + Number(finalPrice);
    } else {
      guestCart.push({
        product_id: product.id,
        batch_id: batch.id,
        amount: Number(finalPrice),
        quantity: Number(quantity),
      });
    }

    setShowCartPopUp(true);
    hidePopupDebounced();
    localStorage.setItem('guestCart', JSON.stringify(guestCart));
    setCartItemsCounter(guestCart.length);
  }

  async function handleCreateCart(user, product, batch, finalPrice, quantity) {
    const existing = userCartBatches.find(cb => cb.batch_id === batch.id);

    let response;
    if (existing) {
      const d = new FormData();
      d.append("cart_batch[quantity]", existing.quantity + quantity);
      d.append("cart_batch[amount]",   Number(existing.amount) + Number(finalPrice));
      response = await api.put(`/api/v1/cart_batches/${existing.id}`, d);
    } else {
      response = await api.post("/api/v1/cart_batches", {
        user_id: user.id,
        batch_id: batch.id,
        amount: finalPrice,
        quantity: quantity,
      });
    }

    if (response.status === 200 || response.status === 201) {
      setShowCartPopUp(true);
      hidePopupDebounced();
      fetchUserCartCount();
      if (window && window.gtag) {
        window.gtag('event', 'add_to_cart', {
          currency: "BRL",
          value: finalPrice,
          'items': [{
            'item_name': product.name,
            'item_id': product.id,
            'price': finalPrice,
            'item_brand': product.brand,
            'quantity': quantity
          }]
        });
      }
      if (typeof window !== "undefined" && window.fbq != null) {
        window.fbq('trackCustom', 'handleCreateCart');
      }
    }
  }


  function mergeGuestCartItems(items) {
    const cartData = localStorage.getItem('guestCart');
    let guestCart = [];
    try {
      if (cartData) guestCart = JSON.parse(cartData) || [];
    } catch (error) {
      console.error("Error parsing guestCart from localStorage:", error);
      guestCart = [];
    }

    items.forEach(({ product_id, batch_id, amount, quantity }) => {
      const idx = guestCart.findIndex(item => item.batch_id === batch_id);
      if (idx >= 0) {
        guestCart[idx].quantity = Number(guestCart[idx].quantity) + Number(quantity);
        guestCart[idx].amount   = Number(guestCart[idx].amount)   + Number(amount);
      } else {
        guestCart.push({ product_id, batch_id, amount: Number(amount), quantity: Number(quantity) });
      }
    });

    localStorage.setItem('guestCart', JSON.stringify(guestCart));
    setCartItemsCounter(guestCart.length);
  }

  // Bulk-adds items produced by the event calculator's "Adicionar tudo ao
  // carrinho" in one action, with a single confirmation popup instead of one
  // per item. items: [{ product_id, product_name, batch_id, amount, quantity }]
  async function handleBulkAddToCart(items) {
    if (!items || items.length === 0) return;

    if (user) {
      // Work off a local copy so two items sharing a batch_id within the same
      // bulk-add (e.g. same product suggested by two result blocks) merge
      // correctly instead of racing against a stale userCartBatches snapshot.
      const working = [...userCartBatches];
      for (const item of items) {
        const idx = working.findIndex(cb => cb.batch_id === item.batch_id);
        if (idx >= 0) {
          const existing = working[idx];
          const d = new FormData();
          d.append("cart_batch[quantity]", existing.quantity + item.quantity);
          d.append("cart_batch[amount]",   Number(existing.amount) + Number(item.amount));
          await api.put(`/api/v1/cart_batches/${existing.id}`, d);
          working[idx] = {
            ...existing,
            quantity: existing.quantity + item.quantity,
            amount: Number(existing.amount) + Number(item.amount),
          };
        } else {
          const response = await api.post("/api/v1/cart_batches", {
            user_id: user.id, batch_id: item.batch_id, amount: item.amount, quantity: item.quantity,
          });
          working.push({ id: response.data.id, batch_id: item.batch_id, quantity: item.quantity, amount: item.amount });
        }
      }
      await fetchUserCartCount();
    } else {
      mergeGuestCartItems(items);
    }

    setShowCartPopUp(true);
    hidePopupDebounced();

    if (window && window.gtag) {
      window.gtag('event', 'add_to_cart', {
        currency: "BRL",
        value: items.reduce((sum, i) => sum + Number(i.amount), 0),
        items: items.map(i => ({
          item_id: i.product_id, item_name: i.product_name, price: i.amount, quantity: i.quantity,
        })),
      });
    }
  }

  function decrementCartCounter() {
    setCartItemsCounter(prevCount => Math.max(prevCount - 1, 0));
    if (!user) {
      const currentCounter = parseInt(localStorage.getItem('cartItems') || '0', 10);
      localStorage.setItem('cartItems', Math.max(currentCounter - 1, 0).toString());
    }
  }

  return (
    <CartContext.Provider
      value={{cartItemsCounter, showCartPopUp, setShowCartPopUp, selectedProduct, handleSetSelectedProduct, decrementCartCounter, handleCreateCart, migrateGuestCartToUser, handleBulkAddToCart}}
    >
      {props.children}
    </CartContext.Provider>
  );
}
